import { useApolloClient, useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { BTC_CONTENT_ASSETS_FRAGMENT } from '@optools/services/operator-gateway/btc/fragments';
import { UPDATE_ASSETS_STATE } from '@optools/services/operator-gateway/btc/mutations';
import {
  AssetState,
  AssetTier,
  MediaAssetsInput,
  Region,
  RegionalAssetInput,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { Layers } from 'lucide-react';
import { AnimatePresence, m as motion } from 'motion/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { BulkAssetUpdatesIdle } from './components/idle';
import { BulkAssetUpdatesProcessing } from './components/processing';
import { BulkAssetUpdatesSuccess } from './components/success';
import { BulkAssetUpdatesWarning } from './components/warning';
import { BulkAssetUpdateFormValues } from '../../schemas/assets';
import { generateAssetKey } from '../../utils/assets';

export interface BulkAssetUpdatesDialogProps {
  mediaIds: string[];
}

export function BulkAssetUpdatesDialog({ mediaIds }: BulkAssetUpdatesDialogProps) {
  const client = useApolloClient();
  const [open, setOpen] = useState(false);

  const [status, setStatus] = useState<'idle' | 'processing' | 'success' | 'warning'>('idle');
  const [assetIds, setAssetIds] = useState<Set<string>>(new Set());
  const [assetState, setAssetState] = useState<AssetState | null>(null);

  const [updateAssetsState, updateAssetsStateResult] = useMutation(UPDATE_ASSETS_STATE, {
    onCompleted: ({ updateAssetsState }) => {
      const failedUpdates = updateAssetsState.filter((item) => !item.success);
      if (failedUpdates.length) {
        toast.error('Partial Batch Update Failures', {
          description: 'Some asset updates failed during submission. Please resubmit the request.',
          duration: Infinity,
        });
      } else {
        setStatus('processing');
      }
    },
  });

  const { loading } = updateAssetsStateResult;

  const handleSubmit = (values: BulkAssetUpdateFormValues) => {
    const allAssetIds = new Set<string>();
    const mediaAssetsInput = mediaIds.reduce<MediaAssetsInput[]>((acc, mediaId) => {
      const contentItem = client.readFragment({
        fragment: BTC_CONTENT_ASSETS_FRAGMENT,
        id: client.cache.identify({ __typename: 'Content', mediaId }),
      });

      if (contentItem) {
        const regionalAssetsMap = contentItem.assets.reduce<Map<string, RegionalAssetInput>>(
          (acc, asset) => {
            if (asset.assetId) {
              if (
                (values.tier === 'ALL' || values.tier === asset.tier) &&
                (values.provider === 'ALL' || values.provider === asset.provider)
              ) {
                const region =
                  asset.tier === AssetTier.RECOVERY && !asset.path.includes('-dr')
                    ? Region.CENTRAL
                    : Region.EAST;
                allAssetIds.add(generateAssetKey(asset));
                acc.set(`${asset.assetId}-${region}`, { assetId: asset.assetId, region });
              }
            }

            return acc;
          },
          new Map(),
        );

        if (regionalAssetsMap.size) {
          acc.push({ mediaId, regionalAssets: Array.from(regionalAssetsMap.values()) });
        }
      }

      return acc;
    }, []);

    setAssetState(values.assetState);
    setAssetIds(allAssetIds);

    updateAssetsState({
      variables: { assetState: values.assetState, mediaAssets: mediaAssetsInput },
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (loading || status === 'processing') return;

    setOpen(nextOpen);
  };

  const handleReset = () => {
    setStatus('idle');
    setAssetIds(new Set());
    setAssetState(null);
    updateAssetsStateResult.reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Layers />
          Bulk Update Assets
        </Button>
      </DialogTrigger>
      <DialogContent onCloseAutoFocus={handleReset}>
        <DialogHeader>
          <DialogTitle>Bulk Update Assets</DialogTitle>
          <DialogDescription>
            {status === 'idle' && 'Select the asset filters and desired state you wish to update'}
            {status === 'processing' && 'Processing asset state updates...'}
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={status}
            animate={{ opacity: 1, transition: { duration: 0.2 }, x: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.1 }, x: -16 }}
            initial={{ opacity: 0, x: 24 }}>
            {status === 'idle' && (
              <BulkAssetUpdatesIdle
                called={updateAssetsStateResult.called}
                loading={loading}
                onSubmit={handleSubmit}
              />
            )}
            {status === 'processing' && (
              <BulkAssetUpdatesProcessing
                assetIds={assetIds}
                assetState={assetState}
                onComplete={() => setStatus('success')}
                onTimeout={() => setStatus('warning')}
              />
            )}
            {status === 'success' && <BulkAssetUpdatesSuccess />}
            {status === 'warning' && <BulkAssetUpdatesWarning />}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
